import { FilterContextProvider } from "@circle-react/components/Modals/SearchResultsModal/FilterBar/FilterContext";
import { SortContextProvider } from "@circle-react/components/Modals/SearchResultsModal/SortContext";
import type { SearchType } from "@circle-react/components/search/types";
import { lazyWithRetry } from "@circle-react/helpers/lazyRetry";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { Modal } from "@circleco/compass/components/Modal";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import { Suspense, useEffect, useState } from "react";

import { t } from "@/i18n-js/instance";

import { useSpotlightSearchUrl } from "./hooks/useSpotlightSearchUrl";
import { SpotlightSearchBodySkeleton } from "./SpotlightSearchBodySkeleton";
import { SpotlightSearchErrorBoundary } from "./SpotlightSearchErrorBoundary";

const SpotlightSearchBody = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "SpotlightSearchBody" */ "./SpotlightSearchBody"
  ).then(module => ({ default: module.SpotlightSearchBody })),
);

const DEFAULT_TAB: SearchType = "posts";

interface SpotlightSearchV4Props {
  isOpen?: boolean;
  onClose: () => void;
  // "modal" (default): renders inside a Compass `Modal.Root` overlay.
  // "page": renders the body directly with no modal wrapper.
  variant?: "modal" | "page";
}

export const SpotlightSearchV4 = ({
  isOpen = true,
  onClose,
  variant = "modal",
}: SpotlightSearchV4Props) => {
  const isPage = variant === "page";
  const { params, syncToUrl, clearFromUrl } = useSpotlightSearchUrl();
  const [submittedQuery, setSubmittedQuery] = useState<string | null>(
    params.query,
  );
  const [activeTab, setActiveTab] = useState<SearchType>(
    params.tab ?? DEFAULT_TAB,
  );
  const isResultsView = submittedQuery !== null;

  const isMobile = useIsMobile();

  useEffect(() => {
    if (isPage) {
      return;
    }
    if (isOpen && submittedQuery === null && !params.isOpen) {
      syncToUrl({ query: null });
    }
  }, [isPage, isOpen, submittedQuery, params.isOpen, syncToUrl]);

  useEffect(() => {
    if (isPage) {
      return;
    }
    if (isOpen && params.query !== null) {
      setSubmittedQuery(params.query);
      setActiveTab(params.tab ?? DEFAULT_TAB);
    }
  }, [isPage, isOpen]);

  const submitQuery = (query: string) => {
    setSubmittedQuery(query);
    syncToUrl({ query, tab: activeTab });
  };

  const changeTab = (tab: SearchType) => {
    setActiveTab(tab);
    syncToUrl({ query: submittedQuery, tab });
  };

  const handleClose = () => {
    setSubmittedQuery(null);
    setActiveTab(DEFAULT_TAB);
    clearFromUrl();
    onClose();
  };

  const body = (
    <div
      className={classNames({
        "flex min-h-[280px] flex-col": !isResultsView && !isPage,
      })}
    >
      <SpotlightSearchErrorBoundary>
        <SpotlightSearchBody
          submittedQuery={submittedQuery}
          activeTab={activeTab}
          onSubmitQuery={submitQuery}
          onTabChange={changeTab}
          onClose={isPage ? onClose : handleClose}
        />
      </SpotlightSearchErrorBoundary>
    </div>
  );

  return (
    <FilterContextProvider>
      <SortContextProvider>
        {isPage ? (
          body
        ) : (
          <Modal.Root
            open={isOpen}
            onOpenChange={open => {
              if (!open) {
                handleClose();
              }
            }}
            size={isResultsView || isMobile ? "full-screen" : "lg"}
            position="top"
            aria-describedby={t("search_v2.search_community_quick")}
          >
            <Modal.Content data-testid="spotlight-search-v4">
              <Modal.Body>
                <div
                  className={classNames({
                    "flex min-h-[280px] flex-col": !isResultsView,
                  })}
                >
                  <SpotlightSearchErrorBoundary>
                    <Suspense fallback={<SpotlightSearchBodySkeleton />}>
                      <SpotlightSearchBody
                        submittedQuery={submittedQuery}
                        activeTab={activeTab}
                        onSubmitQuery={submitQuery}
                        onTabChange={changeTab}
                        onClose={handleClose}
                      />
                    </Suspense>
                  </SpotlightSearchErrorBoundary>
                </div>
              </Modal.Body>
              {(isResultsView || isMobile) && (
                <Modal.Footer
                  primaryAction={{
                    label: t("close"),
                    onClick: handleClose,
                    shouldCloseModal: true,
                  }}
                />
              )}
            </Modal.Content>
          </Modal.Root>
        )}
      </SortContextProvider>
    </FilterContextProvider>
  );
};
